import React, { useCallback } from 'react';
import { Image, useDeviceType, Eye16 as IconShow16 } from '@cian/ui-kit';

import { IArticlesMainAttributesRubricSchema } from '../../repositories/journal/entities/journal/ArticlesMainAttributesRubricSchema';
import { IArticlesMainAttributesSchema } from '../../repositories/journal/entities/journal/ArticlesMainAttributesSchema';
import { IRubricLink, RubricButton } from './components/RubricButton';
import { formatDate } from '../../utils';
import { Card, ICardParams } from '../Card';
import { IconWithCount } from '../IconWithCount';
import { IconMessage16 } from '../Icons';
import * as s from './CardListingItem.css';

export interface ICardListingItemRubric {
  /** Название */
  name: string;
  /** Ссылка */
  url: IRubricLink['url'];
}

export interface ICardListingItem
  extends Partial<Pick<IArticlesMainAttributesSchema, 'title' | 'image' | 'subtitle' | 'datePublish' | 'numViews'>> {
  height?: ICardParams['height'];
  rubric?: ICardListingItemRubric;
  numComments?: IArticlesMainAttributesSchema['commentsCount'];
  url: string;
  children?: React.ReactNode;
  onRubricButtonClick?(e: React.MouseEvent, url: IArticlesMainAttributesRubricSchema['url']): void;
  onClick?(e: React.MouseEvent): void;
}

/**
 * Компонент карточки в списке
 */
export const CardListingItem = ({
  height,
  title,
  url,
  subtitle,
  image,
  rubric,
  datePublish,
  numViews = 0,
  numComments = 0,
  children,
  onRubricButtonClick,
  onClick,
}: ICardListingItem) => {
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';

  const handleArticleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (onClick) {
        onClick(e);
      }
    },
    [onClick],
  );

  const handleRubricClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!rubric) {
        return;
      }

      if (onRubricButtonClick) {
        onRubricButtonClick(e, rubric.url);
      }
    },
    [onRubricButtonClick, rubric],
  );

  return (
    <Card height={height} data-web-ui-test-id="CardListItem" onClick={handleArticleClick}>
      {image && (
        <a href={url} className={s['image-wrapper']} title={title} onClick={handleArticleClick}>
          <Image
            alt={title}
            borderRadius={0}
            objectFit="cover"
            preloader
            src={image}
            theme="light"
            title={title}
            height={isPhone() ? 145 : 180}
          />
        </a>
      )}

      <div className={`${s['content-wrapper']} ${image ? s['_has-image'] : ''}`}>
        {rubric && (
          <div className={s['rubric-button-wrapper']}>
            <RubricButton url={rubric.url} onClick={handleRubricClick}>
              {rubric.name.toLowerCase()}
            </RubricButton>
          </div>
        )}

        {title && (
          <a href={url} className={s['title']} title={title} onClick={handleArticleClick}>
            {title}
          </a>
        )}

        {subtitle && (
          <div className={s['subtitle']} title={subtitle}>
            {subtitle}
          </div>
        )}

        <div className={s['footnote-wrapper']}>
          {children}

          {(datePublish || numViews || numComments) && (
            <div className={s['footnote']}>
              {datePublish && <div className={s['date-publish']}>{formatDate(new Date(datePublish))}</div>}
              {numViews ? <IconWithCount count={numViews} icon={<IconShow16 />} maxCount={999} /> : null}
              {numComments ? <IconWithCount count={numComments} icon={<IconMessage16 />} maxCount={999} /> : null}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
